import { Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
    AnnualReportProvider,
    IFinancialTableData,
    IOptions,
    ISectionData,
    PDFSectionIds,
    PrintUsingPdfTronDto,
} from '@modeso/aumico-lib-annualreport-fe';
import { IBalanceSheetData } from '@modeso/aumico-lib-annualreport-fe/interfaces/pdf-tron/balance-sheet-data.interface';
import { EditSectionTitleComponent } from '@modeso/aumico-lib-core-fe';
import { IPrintOptions } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, tap } from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'annual-report-account-mapping-swiss-display',
    templateUrl: './annual-report-account-mapping-swiss-display.component.html',
    styleUrls: ['./annual-report-account-mapping-swiss-display.component.scss'],
})
export class AccountMappingSwissDisplayComponent implements OnInit {
    @Input()
    public reportType: string | undefined;

    @Input() noPreviousData: boolean;

    @Input() activeLocale: string | undefined;

    @Input() set printOptions(value: IPrintOptions | undefined) {
        if (value != null) {
            this.showRatios = value.displayOptions.showRatios;
            this.showNotes = value.displayOptions.showNotes;
            this.showGroupHeaders = value.displayOptions.showGroupHeaders;
            this.disableBalanceSheetPageBreak = value.displayOptions.disableBalanceSheetPageBreak;
            this.textColor = value.styleOptions.textColor;
            this.tableHeaderColor = value.styleOptions.tableHeaderColor;
            this.tableFooterColor = value.styleOptions.tableFooterColor;
            this.backgroundColor = value.styleOptions.backgroundColor;
            this.backgroundColorPY = value.styleOptions.backgroundColorPY;

            if (
                value.reportSectionsTitles != null &&
                value.reportSectionsTitles[this.reportType] != null &&
                value.reportSectionsTitles[this.reportType][this.activeLocale] != null
            ) {
                this.sectionTitle = value.reportSectionsTitles[this.reportType][this.activeLocale];
            }
        }
    }

    @Output() sectionTitleChanged: EventEmitter<string> = new EventEmitter<string>();

    textColor: string;
    tableFooterColor: string;
    tableHeaderColor: string;
    backgroundColor: string;
    backgroundColorPY: string;
    showRatios: boolean;
    showNotes: boolean;
    showGroupHeaders = false;
    disableBalanceSheetPageBreak = false;
    options: IOptions;
    styles: string[] = [];
    lastSectionIndex = 0;

    @HostBinding('style.--style-1') style1 = 'none';
    @HostBinding('style.--style-2') style2 = 'none';
    @HostBinding('style.--style-3') style3 = 'none';
    @HostBinding('style.--style-4') style4 = 'none';
    @HostBinding('style.--style-5') style5 = 'none';
    @HostBinding('style.--style-6') style6 = 'none';
    @HostBinding('style.--style-7') style7 = 'none';
    @HostBinding('style.--style-8') style8 = 'none';
    @HostBinding('style.--style-9') style9 = 'none';
    @HostBinding('style.--style-10') style10 = 'none';
    public sectionData: IFinancialTableData[][];
    public sectionTitle: string;
    /** FX (REV-21512): the section prints both currencies — set by the mapper, like noPrevious. */
    public isFxFormat = false;

    constructor(
        private readonly annualReportProvider: AnnualReportProvider,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private dialog: MatDialog,
    ) {
        this.addSvgIcon('aumico-pattern-icon-edit-write');
    }

    ngOnInit(): void {
        this.annualReportProvider
            .getPrintView$()
            .pipe(
                filter((printViewData: PrintUsingPdfTronDto) => printViewData != null),
                tap((printViewData: PrintUsingPdfTronDto) => (this.options = printViewData.data.options)),
                map((printViewData: PrintUsingPdfTronDto) => printViewData.data.sections),
                map((sections: ISectionData[]) =>
                    sections.find(
                        (section) =>
                            section.sectionId ===
                            (this.reportType === 'income' ? PDFSectionIds.INCOME : PDFSectionIds.BALANCE),
                    ),
                ),

                map((sections: ISectionData) => {
                    let data: IFinancialTableData[][] = [[]];
                    this.sectionTitle = sections.title;
                    this.isFxFormat = sections.isFxFormat === true;
                    if (this.reportType === 'income') data[0] = sections.data as IFinancialTableData[];
                    else if (this.reportType === 'balance') {
                        data[0] = (sections.data as IBalanceSheetData).assets as IFinancialTableData[];
                        data[1] = (sections.data as IBalanceSheetData).liabilitiesAndEquity as IFinancialTableData[];
                    }
                    return data;
                }),
                untilDestroyed(this),
            )
            .subscribe((items) => {
                if (this.reportType === 'balance') {
                    this.styles.push('upper-alpha');
                    this.styles.push('upper-roman');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                } else {
                    this.styles.push('decimal');
                    this.styles.push('lower-alpha');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                    this.styles.push('decimal');
                }

                for (let i = 0; i < this.styles.length; i++) {
                    this['style' + (i + 1)] = this.styles[i];
                }
                this.sectionData = items;
                this.lastSectionIndex = this.sectionData.length - 1;
            });
    }

    public editSectionTitle(): void {
        const dialogRef = this.dialog.open<EditSectionTitleComponent, string, string | undefined>(
            EditSectionTitleComponent,
            {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: this.sectionTitle,
            },
        );

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result != null) {
                    this.sectionTitle = result;
                    this.sectionTitleChanged.emit(result);
                }
            });
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }
}
